import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Select, Input, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import { useNavigate } from 'react-router-dom';
import { ReloadOutlined } from '@ant-design/icons';
import contractReviewService, { type ContractReview } from '@/services/contractReview.service';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { usePendingApproversMap } from '@/hooks/usePendingApproversMap';

const { Title } = Typography;

const ContractReviewRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [reviewStatus, setReviewStatus] = useState<string | undefined>();
  const [pendingApproval, setPendingApproval] = useState(false);
  const [approvedNotConverted, setApprovedNotConverted] = useState(false);
  const [quotationId, setQuotationId] = useState('');
  const [teamFilter, setTeamFilter] = useState<string | undefined>();

  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });

  const queryParams = useMemo(() => {
    const p: Record<string, string | undefined> = {
      take: '100',
      skip: '0',
    };
    if (reviewStatus) p.review_status = reviewStatus;
    if (teamFilter) p.sales_team_id = teamFilter;
    if (pendingApproval) p.pending_approval = 'true';
    if (approvedNotConverted) p.approved_not_converted = 'true';
    if (quotationId.trim()) p.quotation_id = quotationId.trim();
    return p;
  }, [reviewStatus, pendingApproval, approvedNotConverted, quotationId, teamFilter]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contract-reviews', queryParams],
    queryFn: () => contractReviewService.list(queryParams),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const pendingInstanceIds = useMemo(
    () =>
      rows
        .filter((r) => r.approval_instance_id && r.approval_status === 'Pending')
        .map((r) => r.approval_instance_id as string),
    [rows]
  );
  const { labels: pendingApproverLabels } = usePendingApproversMap(pendingInstanceIds);

  const columns: ColumnsType<ContractReview> = [
    {
      title: 'Review No',
      dataIndex: 'review_number',
      render: (v, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/contract-reviews/${r.id}`)}>
          {v}
        </Button>
      ),
    },
    { title: 'Sales team', dataIndex: 'sales_team_name', render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'review_status', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Approval',
      dataIndex: 'approval_status',
      render: (v, r) => (
        <div>
          <Tag color="blue">{v}</Tag>
          {r.approval_instance_id && v === 'Pending' ? (
            <PendingApproverText compact pendingApproverLabel={pendingApproverLabels[r.approval_instance_id]} />
          ) : null}
        </div>
      ),
    },
    {
      title: 'Risk',
      dataIndex: 'overall_risk_rating',
      render: (v) => v || '—',
    },
    {
      title: 'Stale',
      dataIndex: 'is_stale',
      render: (v) => (v ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Approved',
      dataIndex: 'approved_at',
      render: (v) => (v ? String(v).slice(0, 10) : '—'),
    },
    {
      title: 'Actions',
      key: 'a',
      render: (_, r) => (
        <Button size="small" onClick={() => navigate(`/growth/contract-reviews/${r.id}`)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            Contract Review register
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        </Space>

        <Card size="small" title="Filters">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <div style={{ marginBottom: 4 }}>Review status</div>
              <Select
                allowClear
                placeholder="Any"
                style={{ width: '100%' }}
                value={reviewStatus}
                onChange={setReviewStatus}
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Under Review', label: 'Under Review' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Returned for Correction', label: 'Returned for Correction' },
                  { value: 'Rejected', label: 'Rejected' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} md={6}>
              <div style={{ marginBottom: 4 }}>Quotation ID</div>
              <Input
                placeholder="UUID"
                value={quotationId}
                onChange={(e) => setQuotationId(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={6}>
              <div style={{ marginBottom: 4 }}>Sales team</div>
              <Select
                allowClear
                placeholder="Any"
                style={{ width: '100%' }}
                value={teamFilter}
                onChange={setTeamFilter}
                options={salesTeams.map((t) => ({ value: t.id, label: t.team_name }))}
              />
            </Col>
            <Col xs={24} md={12}>
              <Space wrap style={{ marginTop: 28 }}>
                <Tag.CheckableTag checked={pendingApproval} onChange={setPendingApproval}>
                  Pending approval
                </Tag.CheckableTag>
                <Tag.CheckableTag checked={approvedNotConverted} onChange={setApprovedNotConverted}>
                  Approved, not converted to SO
                </Tag.CheckableTag>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card size="small" title={`Results (${total})`}>
          <Table<ContractReview>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 50, showSizeChanger: true }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default ContractReviewRegisterPage;
